import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Phone } from "lucide-react";
import { BRAND } from "@/lib/brand";
import { scrollToId } from "@/hooks/useLenis";
import { MaskLine } from "@/components/Reveal";
import { useLang } from "@/lib/i18n";

const HERO_IMG =
  "https://images.unsplash.com/photo-1615803697515-3cb782c2a65a?q=80&w=1600&auto=format&fit=crop";

export default function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const textOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="hero"
      ref={ref}
      className="relative min-h-screen overflow-hidden pt-[17rem] md:pt-[25rem]"
      data-testid="hero-section"
    >
      {/* Blue ambient glow */}
      <div className="pointer-events-none absolute -top-40 -left-40 h-[520px] w-[520px] blue-glow" />
      <div className="pointer-events-none absolute top-1/3 right-0 h-[420px] w-[420px] blue-glow opacity-60" />

      <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-10 lg:gap-8 items-center min-h-[calc(100vh-8rem)]">
        {/* Copy */}
        <motion.div
          style={{ y: textY, opacity: textOpacity }}
          className="lg:col-span-7 relative z-10"
        >
          <motion.span
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.7 }}
            className="inline-flex items-center gap-2 border border-[#1478FF]/40 bg-[#1478FF]/10 text-[#7fb4ff] px-4 py-2 text-[11px] font-semibold tracking-[0.28em] uppercase rounded-full mb-8"
            data-testid="hero-badge"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#1478FF] animate-pulse" />
            {t.hero.badge}
          </motion.span>

          <h1 className="font-display font-semibold text-neutral-900 text-[13vw] sm:text-6xl lg:text-[5.4rem] leading-[0.92] tracking-tight">
            <MaskLine delay={0.25}>{t.hero.lines[0]}</MaskLine>
            <MaskLine delay={0.38}>{t.hero.lines[1]}</MaskLine>
            <MaskLine delay={0.51}>
              {t.hero.lastText}
              <span className="text-[#1478FF]">{t.hero.lastAccent}</span>
            </MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-8 text-neutral-600 text-base md:text-lg max-w-lg leading-relaxed"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.7 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <button
              onClick={() => scrollToId("quote")}
              className="group inline-flex items-center gap-2 bg-[#1478FF] hover:bg-[#3B8BFF] active:scale-95 text-white font-semibold px-7 py-4 rounded-full transition-all duration-300 hover:shadow-[0_0_34px_-4px_rgba(20,120,255,0.8)]"
              data-testid="hero-quote-button"
            >
              {t.hero.ctaQuote}
              <ArrowRight
                size={18}
                className="transition-transform group-hover:translate-x-1"
              />
            </button>
            <button
              onClick={() => scrollToId("services")}
              className="inline-flex items-center gap-2 border border-black/20 text-neutral-900 font-semibold px-7 py-4 rounded-full transition-all duration-300 hover:bg-neutral-900 hover:text-white"
              data-testid="hero-services-button"
            >
              {t.hero.ctaServices}
            </button>
          </motion.div>

          <motion.a
            href={`tel:${BRAND.phone}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.7 }}
            className="mt-8 inline-flex items-center gap-3 text-sm text-neutral-600 hover:text-neutral-900 transition-colors"
            data-testid="hero-phone-link"
          >
            <Phone size={16} className="text-[#1478FF]" />
            {BRAND.contactPerson} · {BRAND.phone}
          </motion.a>
        </motion.div>

        {/* Image */}
        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ delay: 0.5, duration: 1.1, ease: [0.76, 0, 0.24, 1] }}
            className="relative h-[360px] sm:h-[460px] lg:h-[560px] w-full overflow-hidden border border-black/10"
          >
            <motion.img
              style={{ y: imgY, scale: imgScale }}
              src={HERO_IMG}
              alt="Professional commercial cleaning"
              className="absolute inset-0 h-[130%] w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
            <div className="absolute inset-0 ring-1 ring-inset ring-[#1478FF]/20" />
          </motion.div>

          {/* Floating stat card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.7 }}
            className="absolute -bottom-6 -left-4 md:left-auto md:-right-6 bg-neutral-900 border border-black/10 px-6 py-5 shadow-xl"
            data-testid="hero-stat-card"
          >
            <p className="font-display text-4xl font-semibold text-white">
              100<span className="text-[#1478FF]">%</span>
            </p>
            <p className="text-xs text-zinc-400 tracking-wide uppercase mt-1">
              {t.hero.statLabel}
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
